import { useState, useCallback, useEffect } from "react";
import API from "../config/api.js";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function isEditable(draft) {
  if (!draft?.date || !draft?.time) return false;
  const postTime = new Date(`${draft.date}T${draft.time}Z`);
  return (
    postTime > new Date() &&
    !draft.facebookpostid &&
    !draft.instagrampostid &&
    !draft.linkedinpostid &&
    !draft.tiktokpostid
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Media carousel
// ─────────────────────────────────────────────────────────────────────────────

function DraftCarousel({ draft }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const items = draft?.items || [];

  if (!items.length) {
    return <img src="/assets/img/350x350.png" alt="" className="w-100 bg-light rounded-3" />;
  }

  const item = items[activeIndex];
  const isReel = item?.type === "reel";
  const thumbnail = `${API.baseUrl}/upload/${item?.type}/${item?.id}/image/original.jpg`;

  const videoSrc = isReel
    ? `${API.baseUrl}/${draft.reel_filewithmusic || draft.reel_file || `upload/reel/${item.id}/file/file.mp4`}`
    : null;

  const handlePlayPause = (e) => {
    e.preventDefault();
    if (videoSrc) setIsPlaying((v) => !v);
  };

  return (
    <div className="position-relative">
      <div className="rounded-3 overflow-hidden bg-light">
        {isReel ? (
          <div className="position-relative text-center" style={{ aspectRatio: "9/16" }}>
            {!isPlaying && (
              <img
                src={thumbnail}
                alt=""
                className="h-100 w-auto rounded-3 bg-light"
                loading="lazy"
              />
            )}

            {isPlaying && videoSrc && (
              <video
                className="w-100 h-100 rounded-3"
                autoPlay
                onEnded={() => setIsPlaying(false)}
                style={{ objectFit: "cover" }}
              >
                <source src={videoSrc} type="video/mp4" />
              </video>
            )}

            <a
              href="#preview"
              className="position-absolute top-50 start-50 translate-middle"
              onClick={handlePlayPause}
              style={{ zIndex: 4 }}
            >
              <div className="d-inline-flex align-items-center justify-content-center bg-white bg-opacity-25 rounded-circle">
                <img
                  src={isPlaying ? "/assets/img/icon/pause.png" : "/assets/img/icon/play.png"}
                  width="70"
                  height="70"
                  alt={isPlaying ? "Pause" : "Play"}
                />
              </div>
            </a>
          </div>
        ) : (
          <img
            src="/assets/img/864x1080-transparent.png"
            style={{
              backgroundImage: `url(${thumbnail})`,
              backgroundColor: "#f1f1f1",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
            width="864"
            height="1080"
            className="rounded-3 w-100 h-100"
            alt=""
          />
        )}
      </div>

      {/* Date/time edit inputs */}
      {isEditable(draft) && (
        <div className="m-3 position-absolute bottom-0 start-0 end-0 text-end">
          <input type="hidden" name="iddraft" value={draft.iddraft} />
          <input
            className="form-control py-3 me-2 d-inline w-auto"
            name="date"
            type="date"
            min={new Date().toISOString().split("T")[0]}
            defaultValue={draft.date}
            required
          />
          <input
            className="form-control py-3 d-inline w-auto"
            name="time"
            type="time"
            defaultValue={draft.time?.slice(0, 5)}
            required
          />
        </div>
      )}

      {/* Carousel controls */}
      {items.length > 1 && (
        <>
          <button
            className="carousel-control-prev"
            type="button"
            onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
          >
            <span className="carousel-control-prev-icon" aria-hidden="true" />
            <span className="visually-hidden">Previous</span>
          </button>
          <button
            className="carousel-control-next"
            type="button"
            onClick={() => setActiveIndex((i) => Math.min(items.length - 1, i + 1))}
          >
            <span className="carousel-control-next-icon" aria-hidden="true" />
            <span className="visually-hidden">Next</span>
          </button>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Social account row
// ─────────────────────────────────────────────────────────────────────────────

function SocialRow({ platform, image, fallback, name, error, isFirst, enabled }) {
  if (!enabled) return null;
  return (
    <div>
      {!isFirst && <hr />}
      <div className="d-inline-block position-relative me-3">
        <img
          src={image || fallback}
          onError={(e) => (e.target.src = fallback)}
          alt=""
          className="rounded-circle"
          width="48"
          height="48"
        />
        <span className="position-absolute top-50 start-100 translate-middle">
          <img
            src={`/assets/img/${platform}.png`}
            style={{ height: 30, width: 30 }}
            alt={platform}
          />
        </span>
      </div>
      <span className="fw-bold ms-2">{name || platform}</span>
      {error && (
        <span className="text-danger ms-2" title={error}>
          <i className="fi fi-rr-exclamation fs-5" />
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Metrics row
// ─────────────────────────────────────────────────────────────────────────────

function MetricsRow({ platform, metrics }) {
  if (!metrics) return null;
  return (
    <div className={`metrics ${platform} mt-3 d-none`}>
      <div className="row text-center g-2">
        {metrics.likes != null && (
          <div className="col">
            <div className="small text-muted">Likes</div>
            <div className="fw-bold likes">{metrics.likes}</div>
          </div>
        )}
        {metrics.comments != null && (
          <div className="col">
            <div className="small text-muted">Comments</div>
            <div className="fw-bold comments">{metrics.comments}</div>
          </div>
        )}
        {metrics.shares != null && (
          <div className="col">
            <div className="small text-muted">Shares</div>
            <div className="fw-bold shares">{metrics.shares}</div>
          </div>
        )}
        {metrics.views != null && (
          <div className="col">
            <div className="small text-muted">Views</div>
            <div className="fw-bold views">{metrics.views}</div>
          </div>
        )}
        {metrics.impressions != null && (
          <div className="col">
            <div className="small text-muted">Impressions</div>
            <div className="fw-bold impressions">{metrics.impressions}</div>
          </div>
        )}
        {metrics.reach != null && (
          <div className="col">
            <div className="small text-muted">Reach</div>
            <div className="fw-bold reach">{metrics.reach}</div>
          </div>
        )}
        {metrics.engagement != null && (
          <div className="col">
            <div className="small text-muted">Engagement</div>
            <div className="fw-bold engagement">{metrics.engagement}</div>
          </div>
        )}
        {metrics.permalink && (
          <div className="col-12 text-end">
            <a href={metrics.permalink} target="_blank" rel="noreferrer" className="permalink small">
              View post <i className="fi fi-rr-arrow-up-right-from-square" />
            </a>
          </div>
        )}
        {metrics.status === "error" && (
          <div className="col-12 text-center text-danger small">
            Unable to get some metrics.{" "}
            <a href="/account">Reconnect your account</a>.
            <div className="text-truncate text-muted">{metrics.message}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * DraftModal
 *
 * Props:
 *  onDraftEdited(iddraft) — called after save so parent can refresh
 *  onDraftRemoved(iddraft) — called after unschedule removes from calendar
 */
export default function DraftModal({ iddraft, open, onClose, onDraftEdited, onDraftRemoved }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unpostOpen, setUnpostOpen] = useState(false);
  const [unposting, setUnposting] = useState(false);
  const [metrics, setMetrics] = useState({ facebook: null, instagram: null, linkedin: null, tiktok: null });
  const [totalLikes, setTotalLikes] = useState(0);
  const [totalComments, setTotalComments] = useState(0);

  // ── Load draft ─────────────────────────────────────────────────────────────
  const loadDraft = useCallback(async (iddraft) => {
    setLoading(true);
    setDraft(null);
    setMetrics({ facebook: null, instagram: null, linkedin: null, tiktok: null });
    setTotalLikes(0);
    setTotalComments(0);

    try {
      const fd = new FormData();
      fd.append("iddraft", iddraft);
      const res = await fetch(`${API.drafts}?load=true`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json();
      setDraft(data);

      // fetch metrics in parallel
      if (data.facebookpostid)  fetchFacebookMetrics(data.facebookpostid, data.type);
      if (data.instagrampostid) fetchInstagramMetrics(data.instagrampostid, data.type);
      if (data.linkedinpostid)  fetchLinkedinMetrics(data.linkedinpostid);
      if (data.tiktokpostid)    fetchTiktokMetrics(data.tiktokpostid);
    } catch (err) {
      console.error("DraftModal loadDraft:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Expose globally for legacy callers (window._draft_load)
  useEffect(() => {
    window._draft_load = (id) => loadDraft(id);
    return () => { delete window._draft_load; };
  }, [loadDraft]);

  // prop-driven: load when open + iddraft changes
  useEffect(() => {
    if (open && iddraft) loadDraft(iddraft);
  }, [open, iddraft, loadDraft]);

  // ── Metrics ────────────────────────────────────────────────────────────────
  async function fetchFacebookMetrics(facebookpostid, type) {
    try {
      const fd = new FormData();
      fd.append("facebookpostid", facebookpostid);
      fd.append("type", type);
      const res = await fetch(`${API.baseUrl}/meta?facebookPostMetrics`, {
        method: "POST", credentials: "include", body: fd,
      });
      const m = await res.json();
      setMetrics((prev) => ({ ...prev, facebook: m }));
      setTotalLikes((prev) => prev + (Number(m.likes) || 0));
      setTotalComments((prev) => prev + (Number(m.comments) || 0));
    } catch (err) { console.error("Facebook metrics:", err); }
  }

  async function fetchInstagramMetrics(instagrampostid, type) {
    try {
      const fd = new FormData();
      fd.append("instagrampostid", instagrampostid);
      fd.append("type", type);
      const res = await fetch(`${API.baseUrl}/meta?instagramPostMetrics`, {
        method: "POST", credentials: "include", body: fd,
      });
      const m = await res.json();
      setMetrics((prev) => ({ ...prev, instagram: m }));
      setTotalLikes((prev) => prev + (Number(m.likes) || 0));
      setTotalComments((prev) => prev + (Number(m.comments) || 0));
    } catch (err) { console.error("Instagram metrics:", err); }
  }

  async function fetchLinkedinMetrics(linkedinpostid) {
    try {
      const fd = new FormData();
      fd.append("linkedinpostid", linkedinpostid);
      const res = await fetch(`${API.baseUrl}/linkedin?linkedinPostMetrics`, {
        method: "POST", credentials: "include", body: fd,
      });
      const m = await res.json();
      setMetrics((prev) => ({ ...prev, linkedin: m }));
      setTotalLikes((prev) => prev + (Number(m.likes) || 0));
      setTotalComments((prev) => prev + (Number(m.comments) || 0));
    } catch (err) { console.error("LinkedIn metrics:", err); }
  }

  async function fetchTiktokMetrics(tiktokpostid) {
    try {
      const fd = new FormData();
      fd.append("tiktokpostid", tiktokpostid);
      const res = await fetch(`${API.baseUrl}/tiktok?tiktokPostMetrics`, {
        method: "POST", credentials: "include", body: fd,
      });
      const m = await res.json();
      setMetrics((prev) => ({ ...prev, tiktok: m }));
      setTotalLikes((prev) => prev + (Number(m.likes) || 0));
      setTotalComments((prev) => prev + (Number(m.comments) || 0));
    } catch (err) { console.error("TikTok metrics:", err); }
  }

  // ── Save (quickEdit) ───────────────────────────────────────────────────────
  async function handleSave(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    try {
      await fetch(`${API.drafts}?quickEdit`, {
        method: "POST", credentials: "include", body: fd,
      });
      onClose?.();
      onDraftEdited?.(draft?.iddraft);
    } catch (err) {
      console.error("DraftModal save:", err);
    }
  }

  // ── Unschedule ─────────────────────────────────────────────────────────────
  async function handleUnpost() {
    if (!draft?.iddraft) return;
    setUnposting(true);
    try {
      const fd = new FormData();
      fd.append("iddraft", draft.iddraft);
      await fetch(`${API.drafts}?unpostDraft`, {
        method: "POST", credentials: "include", body: fd,
      });
      setUnpostOpen(false);
      onClose?.();
      onDraftRemoved?.(draft.iddraft);
    } catch (err) {
      console.error("DraftModal unpost:", err);
    } finally {
      setUnposting(false);
    }
  }

  // ── Use Post (reuse as draft) ──────────────────────────────────────────────
  function handleUsePost() {
    onClose?.();
    window._draft_edit?.(draft?.iddraft, true, true);
  }

  // ── Derived ───────────────────────────────────────────────────────────────
  const editable = draft ? isEditable(draft) : false;
  const hasAnyPostId =
    draft?.facebookpostid || draft?.instagrampostid || draft?.linkedinpostid || draft?.tiktokpostid;
  const modalSize = draft?.type === "reel" ? "modal-lg" : "modal-xl";

  const hasFacebook  = Number(draft?.facebook) === 1;
  const hasInstagram = Number(draft?.instagram) === 1;
  const hasLinkedin  = Number(draft?.linkedin) === 1;
  const hasTiktok    = Number(draft?.tiktok) === 1;

  // isFirst: primeira rede habilitada não recebe <hr />
  const facebookIsFirst  = hasFacebook;
  const instagramIsFirst = !hasFacebook && hasInstagram;
  const linkedinIsFirst  = !hasFacebook && !hasInstagram && hasLinkedin;
  const tiktokIsFirst    = !hasFacebook && !hasInstagram && !hasLinkedin && hasTiktok;

  // ── Render ────────────────────────────────────────────────────────────────
  if (!open) return null;

  return (
    <>
      {/* ── Backdrop ── */}
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => onClose?.()} />

      {/* ── Draft modal ── */}
      <div
        className="modal fade show d-block"
        id="modal-draft-posted"
        tabIndex={-1}
        style={{ zIndex: 1050 }}
        aria-modal="true"
        role="dialog"
      >
        <div className={`modal-dialog ${modalSize} modal-dialog-centered`}>
          <div className="modal-content border-0 rounded-4">
            <div className="modal-body p-2">

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status" />
                </div>
              ) : draft ? (
                <form action={`${API.drafts}?quickEdit`} method="post" onSubmit={handleSave}>
                  {/* Mobile close */}
                  <div className="d-block d-sm-none p-3 text-end">
                    <button type="button" className="btn-close" onClick={() => onClose?.()} aria-label="Close" />
                  </div>

                  <div className="row g-3">
                    {/* ── Left: media ── */}
                    <div className="col-md-5">
                      <DraftCarousel draft={draft} />
                    </div>

                    {/* ── Right: content ── */}
                    <div className="col-md-7">
                      <div className="d-flex flex-column h-100">
                        <div className="flex-grow-1 p-3 pb-0">
                          {/* Desktop close */}
                          <button
                            type="button"
                            className="d-none d-sm-block btn btn-close bg-white rounded-circle p-3 position-absolute"
                            style={{ right: "-55px", height: "22px", marginTop: "-24px" }}
                            onClick={() => onClose?.()}
                            aria-label="Close"
                          />

                          {/* ── Title (datetime) ── */}
                          <p className="small text-end text-muted mb-2">{draft._userdatetime}</p>

                          {/* ── Social accounts ── */}
                          <div className="social">
                            <SocialRow
                              platform="facebook"
                              enabled={hasFacebook}
                              image={draft.facebookpageimage}
                              fallback="/assets/img/profile-facebook.png"
                              name={draft.facebookpagename}
                              error={draft.facebookautomationstatus === "error" ? draft.facebookautomationerror : null}
                              isFirst={facebookIsFirst}
                            />
                            <SocialRow
                              platform="instagram"
                              enabled={hasInstagram}
                              image={draft.instagramprofilepicture}
                              fallback="/assets/img/profile-instagram.png"
                              name={draft.instagramusername}
                              error={draft.instagramautomationstatus === "error" ? draft.instagramautomationerror : null}
                              isFirst={instagramIsFirst}
                            />
                            <SocialRow
                              platform="linkedin"
                              enabled={hasLinkedin}
                              image={draft.linkedinpageimage}
                              fallback="/assets/img/profile-linkedin.png"
                              name={draft.linkedinpagename}
                              error={draft.linkedinautomationstatus === "error" ? draft.linkedinautomationerror : null}
                              isFirst={linkedinIsFirst}
                            />
                            <SocialRow
                              platform="tiktok"
                              enabled={hasTiktok}
                              image={draft.tiktokprofilepicture}
                              fallback="/assets/img/profile-tiktok.png"
                              name={draft.tiktokusername}
                              error={draft.tiktokautomationstatus === "error" ? draft.tiktokautomationerror : null}
                              isFirst={tiktokIsFirst}
                            />
                          </div>

                          {/* ── Content ── */}
                          {editable ? (
                            <textarea name="content" className="form-control mt-3" rows={12} defaultValue={draft.content} required />
                          ) : (
                            <div className="overflow-auto mt-3" style={{ maxHeight: 300 }}>
                              <p className="text-muted" style={{ whiteSpace: "pre-line" }}>{draft.content}</p>
                            </div>
                          )}

                          {/* ── Metrics ── */}
                          <MetricsRow platform="facebook"  metrics={metrics.facebook} />
                          <MetricsRow platform="instagram" metrics={metrics.instagram} />
                          <MetricsRow platform="linkedin"  metrics={metrics.linkedin} />
                          <MetricsRow platform="tiktok"    metrics={metrics.tiktok} />
                        </div>

                        {/* ── Footer actions ── */}
                        <div className="px-3 py-2">
                          <div className="row align-items-end">
                            <div className="col-auto">
                              {/* Engagement totals */}
                              {!editable && (
                                <>
                                  <button type="button" className="btn btn-light border rounded-5" style={{ background: "#f7f5fd", cursor: "default" }}>
                                    <i className="fi fi-rr-heart fs-5" />
                                  </button>
                                  <span className="ms-2 me-2 text-muted d-inline-block" style={{ minWidth: 14 }}>
                                    {totalLikes || ""}
                                  </span>

                                  <button type="button" className="btn btn-light border rounded-5" style={{ background: "#f7f5fd", cursor: "default" }}>
                                    <i className="fi fi-rr-comment fs-5" />
                                  </button>
                                  <span className="ms-2 me-2 text-muted d-inline-block" style={{ minWidth: 14 }}>
                                    {totalComments || ""}
                                  </span>

                                  {/* Open on social media links */}
                                  {hasAnyPostId && (
                                    <div className="dropdown d-inline-block">
                                      <button className="btn btn-light border rounded-5 me-2" type="button" data-bs-toggle="dropdown" style={{ background: "#f7f5fd" }}>
                                        <i className="fi fi-rr-paper-plane fs-5" />
                                      </button>
                                      <ul className="dropdown-menu">
                                        {draft.facebookpostid && (
                                          <li>
                                            <a className="dropdown-item" target="_blank" rel="noreferrer" href={`https://www.facebook.com/${draft.facebookpostid}`}>
                                              Open on Facebook
                                            </a>
                                          </li>
                                        )}
                                        {draft.instagrampostid && (
                                          <li>
                                            <a className="dropdown-item" target="_blank" rel="noreferrer" href={`${API.baseUrl}/meta?instagramPost&instagrampostid=${draft.instagrampostid}`}>
                                              Open on Instagram
                                            </a>
                                          </li>
                                        )}
                                        {draft.linkedinpostid && (
                                          <li>
                                            <a className="dropdown-item" target="_blank" rel="noreferrer" href={`https://www.linkedin.com/feed/update/${draft.linkedinpostid}`}>
                                              Open on LinkedIn
                                            </a>
                                          </li>
                                        )}
                                        {draft.tiktokpostid && (
                                          <li>
                                            <a className="dropdown-item" target="_blank" rel="noreferrer" href={`https://www.tiktok.com/@${draft.tiktokusername}/video/${draft.tiktokpostid}`}>
                                              Open on TikTok
                                            </a>
                                          </li>
                                        )}
                                      </ul>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            <div className="col text-end">
                              {editable ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={() => setUnpostOpen(true)}
                                  >
                                    Unschedule
                                  </button>
                                  <button type="submit" className="btn btn-primary">Save</button>
                                </>
                              ) : (
                                <button type="button" className="btn btn-primary" onClick={handleUsePost}>
                                  Use Post
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* ── Unschedule confirmation modal ── */}
      {unpostOpen && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} />
          <div className="modal fade show d-block" id="modal-draft-unpost" tabIndex={-1} style={{ zIndex: 1070 }} aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg rounded-4">
                <div className="modal-header">
                  <h5 className="modal-title fs-6">Unschedule Post Confirmation</h5>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to unschedule this post?</p>
                  <p>This action will:</p>
                  <ul>
                    <li>Restore the post as a draft</li>
                    <li>Unschedule the post from social media</li>
                  </ul>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={handleUnpost} disabled={unposting}>
                    {unposting ? "Unscheduling…" : "Unpost and Restore as Draft"}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setUnpostOpen(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}